import { useContext, useEffect, useState } from "react";
import { ChartNoAxesColumn, ChevronsUpDown, CircleHelp, Settings } from "lucide-react";

import { AppStateContext } from "@/state/AppStateContext";
import { CurrentUser } from "@/api/models";
import { doTrackEvent } from "@/utils/utils";
import SettingsView from "./Settings";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

type SettingsTab = 'general' | 'profile' | 'personalization' | 'usage';

const NavUser = () => {
  const appStateContext = useContext(AppStateContext);
  const handleTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => doTrackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [initials, setInitials] = useState<string>("");
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const usage = appStateContext?.state.weeklyUsage ?? null;
  const [usageLabel, setUsageLabel] = useState<string>(""); 
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    if (appStateContext?.state.currentUser) {
      setUser(appStateContext?.state.currentUser);
      setInitials(getUserInitials(appStateContext?.state.currentUser.name));
    }
  }, [appStateContext?.state.currentUser]);

  useEffect(() => {
    
  }, [appStateContext?.state.frontendSettings, appStateContext?.state.theme]);
    
  useEffect(() => {
    if (usage && usage.limit > 0) {
      const consumed = usage.used + (usage.reserved ?? 0);
      const displayedConsumed = usage.used > 0 ? consumed : usage.used;
      const usedPct = Math.floor((Math.min(displayedConsumed, usage.limit) / usage.limit) * 100);
      setUsageLabel(`${usedPct}%`);
    }
  }, [usage]);

  // Generate user initials from name
  const getUserInitials = (name: string | undefined): string => {
    if (!name) return "??";
    const parts = name.split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  if (!user) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="btn btn-ghost w-full px-0" onClick={() => handleTrackEvent('click', 'user_menu_open', 'User Menu - Open', '', '', '')}>
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage src={user.photo || ""} alt={user.name} />
              <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{user.name}</span>
              <span className="truncate text-xs">{user.email}</span>
            </div>
            <ChevronsUpDown className="ml-auto size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-secondary border-transparent rounded-3xl shadow-short p-3 user-menu">
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.photo || ""} alt={user.name} />
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuItem className="rounded-xl hover:bg-neutral focus:bg-neutral cursor-pointer p-2" onClick={() => { setActiveTab('usage'); setIsSettingsDialogOpen(true); handleTrackEvent('click', 'usage_open', 'Usage - Open', '', '', ''); }}>
            <div className="flex flex-row items-center justify-between w-full">
              <div className="flex">
                <ChartNoAxesColumn className="me-4" size={18} />
                Usage
              </div>
              <div>{usageLabel}</div>
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem className="rounded-xl hover:bg-neutral focus:bg-neutral cursor-pointer p-2" onClick={() => { setActiveTab('general'); setIsSettingsDialogOpen(true); handleTrackEvent('click', 'settings_open', 'Settings - Open', '', '', ''); }}>
            <div className="flex flex-row items-center w-full">
              <Settings className="me-4" size={18} />
              Settings
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem className="rounded-xl hover:bg-neutral focus:bg-neutral cursor-pointer p-2" onClick={() => { handleTrackEvent('click', 'help', 'Help', '', '', ''); window.open(`https://smsgroupgmbh.sharepoint.com/sites/intranet-content/SitePages/AI.aspx?xsdata=MDV8MDJ8fGYwOGFlYzllNzBjYzRlYTQ5NWNkMDhkZDcxMTNlOGQ5fDA2OWRiOTE0YzU0ODQ4OGZiOWNiYjUzNDA1YTA1ZDQ3fDB8MHw2Mzg3OTEwNTI0ODEzNjk1NDl8VW5rbm93bnxWR1ZoYlhOVFpXTjFjbWwwZVZObGNuWnBZMlY4ZXlKV0lqb2lNQzR3TGpBd01EQWlMQ0pRSWpvaVYybHVNeklpTENKQlRpSTZJazkwYUdWeUlpd2lWMVFpT2pFeGZRPT18MXxMMk5vWVhSekx6RTVPbTFsWlhScGJtZGZUWHBKTlUxRVVtdE5SMWwwVG5wQ2FscFRNREJPZWxFeVRGZEdiVnBxUVhSTmFrVjRUVzFSZDFreVVtMU9ha0V3UUhSb2NtVmhaQzUyTWk5dFpYTnpZV2RsY3k4eE56UXpOVEE0TkRRM01EWTJ8MjdhMzJlODNjZDhhNDEyNDk1Y2QwOGRkNzExM2U4ZDl8N2I0ZDdmYzdlY2E1NDNkMThmYzJkZjFiYzFhMWJmNDk%3D&sdata=S2Jaa1cyRmJzbCs2WDRZRDF0cFdIdzU3TElqdW1tRjhWUFlwSEdLaHVtZz0%3D&ovuser=069db914-c548-488f-b9cb-b53405a05d47%2Cej.cox%40sms-group.com&OR=Teams-HL&CT=1743508966124&clickparams=eyJBcHBOYW1lIjoiVGVhbXMtRGVza3RvcCIsIkFwcFZlcnNpb24iOiI0OS8yNTAzMDIwMTAxMCIsIkhhc0ZlZGVyYXRlZFVzZXIiOmZhbHNlfQ%3D%3D`, `_blank`); }}>
            <div className="flex flex-row items-center w-full">
              <CircleHelp className="help cursor-pointer me-4" size={18} />
              Help
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SettingsView isSettingsDialogOpen={isSettingsDialogOpen} setIsSettingsDialogOpen={setIsSettingsDialogOpen} activeTab={activeTab} setActiveTab={setActiveTab} />
    </>
  );
};

export default NavUser;
